/**
 * External dependencies
 */
import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  YAxis,
  CartesianGrid,
} from "recharts";
import { size, get } from "lodash";

/**
 * Internal dependencies
 */
import { COLORS, COMPARISON_COLORS } from "./constants";
import {
  toYAxisFormatter,
  formatResultSetToShortTitle,
  formatMaybeDateString,
  toNormalizedResultData,
  toNormalizedSeriesNames,
  isInFirstHalf,
} from "./utils";

import EmptyContent from "./base/empty-content";
import { CustomTooltip } from "../custom-tooltip";
import { CustomLegend } from "../custom-legend";

const LineChartRenderer = ({
  resultSet,
  points,
  colorTheme,
  hideLegend = false,
  axisTickAngle = 0,
  compare,
  normalizeData = toNormalizedResultData,
  onActiveDot = undefined,
}) => {
  let formatter = toYAxisFormatter(resultSet);

  let data = normalizeData(resultSet);

  if (size(data) === 0) {
    return <EmptyContent />;
  }

  const normalizedSeries = toNormalizedSeriesNames(resultSet);
  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      className="chart__line-chart"
    >
      <LineChart
        data={data}
        margin={{ top: 20, right: 30, bottom: 10, left: 10 }}
      >
        <CartesianGrid stroke="#eee" vertical={false} />
        <Tooltip dataKey="" content={CustomTooltip} formatter={formatter} />
        <YAxis
          tickFormatter={formatter}
          tickLine={false}
          axisLine={false}
          tick={{ fill: "#33475b", fontSize: 10 }}
        />
        {hideLegend ? null : (
          <Legend
            verticalAlign="top"
            align="left"
            iconType="circle"
            wrapperStyle={{ paddingBottom: 35 }}
            iconSize={10}
            content={(p) => <CustomLegend {...p} isCompare={compare} />}
          />
        )}
        <XAxis
          allowDataOverflow={false}
          stroke="#7c98b6"
          domain={[0, 0]}
          dataKey="x"
          tickLine={false}
          height={25}
          tickMargin={10}
          angle={axisTickAngle}
          tick={{ fill: "#33475b", fontSize: 10 }}
          tickFormatter={(v) => formatMaybeDateString(v)}
        />
        {normalizedSeries.map((series, i) => {
          const isFirstHalf = isInFirstHalf(normalizedSeries, i);

          const colors =
            compare === true
              ? isFirstHalf
                ? COLORS
                : COMPARISON_COLORS
              : COLORS;
          return (
            <Line
              key={series.key}
              dot={points}
              strokeDasharray={isFirstHalf ? undefined : "5 2"}
              strokeWidth={isFirstHalf ? 1 : 2}
              dataKey={series.key}
              name={formatResultSetToShortTitle(resultSet.queryType, series)}
              stroke={get(colors, [colorTheme || "default", i])}
              fill={get(colors, [colorTheme || "default", i])}
              fillOpacity={!compare || isFirstHalf ? 1 : 0.4}
              strokeOpacity={!compare || isFirstHalf ? 1 : 0.4}
              activeDot={{
                onClick: onActiveDot,
                cursor: "pointer",
              }}
            ></Line>
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default LineChartRenderer;
